import { ApolloClient, ApolloLink, InMemoryCache, NormalizedCacheObject } from '@apollo/client';
import { RetryLink } from '@apollo/client/link/retry';
import { WebSocketLink } from '@apollo/client/link/ws';
import { SubscriptionClient } from 'subscriptions-transport-ws';
import { gzip, ungzip } from 'pako';

import pickBy from 'lodash/pickBy';
import identity from 'lodash/identity';

import config from '../config';
import DurableMutationsLink from './DurableMutationsLink';
import isMutation from './isMutation';
import { LocalStorageOperationsRepository } from './OperationsRepository';
import type {
  ConnectionType,
  ConnectionParams,
  OnUnauthorizedCallback,
  OnConnectedCallback,
  OnDisconnectedCallback,
  OnUnrecoverableErrorCallback,
  RetryableError,
} from './apolloClient.types';

const RESPONSE_UNAUTHORIZED = 'Unauthorized';

const NativeWebSocket = window.WebSocket || (window.MozWebSocket as typeof WebSocket | undefined) || window.WebSocket;

const defaultConnectionType: ConnectionType =
  !import.meta.env?.MODE || import.meta.env.MODE === 'development' ? 'text' : 'binary';

class MessageQueue {
  private nextReceivedMessageId = 1;
  private lastProcessedMessageId = 0;
  private indexedMessageQueue: Record<number, (() => void) | undefined> = {};

  getNextId(): number {
    return this.nextReceivedMessageId++;
  }

  process(messageId: number, fn: () => void): void {
    this.indexedMessageQueue[messageId] = fn;

    while (true) {
      const next = this.indexedMessageQueue[this.lastProcessedMessageId + 1];

      if (!next) {
        break;
      }

      next();

      this.indexedMessageQueue[this.lastProcessedMessageId + 1] = undefined;

      this.lastProcessedMessageId += 1;
    }
  }
}

class MyWebSocket extends NativeWebSocket {
  messageQueue = new MessageQueue();

  send(data: string | ArrayBufferLike | Blob | ArrayBufferView): void {
    if (typeof data === 'string') {
      super.send(gzip(data));
    } else {
      super.send(data);
    }
  }

  set onmessage(handler: ((this: WebSocket, ev: MessageEvent) => void) | null) {
    super.onmessage = (ev: MessageEvent): void => {
      const messageId = this.messageQueue.getNextId();

      if (ev.data) {
        if (ev.data instanceof Blob) {
          const reader = new FileReader();

          reader.onload = () => {
            if (reader.result && reader.result instanceof ArrayBuffer) {
              const data = ungzip(new Uint8Array(reader.result), { to: 'string' });
              this.messageQueue.process(messageId, () => handler?.call(this, { ...ev, data } as MessageEvent));
            }
          };

          reader.readAsArrayBuffer(ev.data);
        } else if (ev.data instanceof Object && ev.data.arrayBuffer instanceof Function) {
          ev.data.arrayBuffer().then((buffer: ArrayBuffer) => {
            const data = ungzip(new Uint8Array(buffer), { to: 'string' });
            this.messageQueue.process(messageId, () => handler?.call(this, { ...ev, data } as MessageEvent));
          });
        } else if (ev.data instanceof ArrayBuffer) {
          const data = ungzip(new Uint8Array(ev.data), { to: 'string' });
          this.messageQueue.process(messageId, () => handler?.call(this, { ...ev, data } as MessageEvent));
        } else {
          try {
            this.messageQueue.process(messageId, () => handler?.call(this, ev));
          } finally {
            console.warn('Unexpected type of WS data: ', typeof ev.data);
          }
        }
      } else {
        this.messageQueue.process(messageId, () => handler?.call(this, ev));
      }
    };
  }
}

// Detect if an error represents an unrecoverable connection error
// These errors should not trigger reconnection attempts
function isUnrecoverableError(error: unknown): boolean {
  const err: any = error as any;
  if (!err) return false;

  // Check for connection_error type with specific payloads
  if (err?.type === 'connection_error') {
    const payload = err?.payload;
    if (typeof payload === 'string') {
      const lower = payload.toLowerCase();
      // Unauthorized IP Address is unrecoverable
      if (lower.includes('unauthorized ip')) return true;
    }
    return true; // All connection_error types are unrecoverable
  }

  return false;
}

// Detect if an error represents a forbidden/unauthorized condition
function isForbiddenOrUnauthorized(error: unknown): boolean {
  const err: any = error as any;
  if (!err) return false;

  // Derive a safe message string
  let message = '';
  if (typeof err?.message === 'string') {
    message = err.message;
  } else if (typeof err?.networkError?.message === 'string') {
    message = err.networkError.message;
  } else if (typeof err?.toString === 'function') {
    message = String(err.toString());
  }

  const statusCode: number | undefined = err?.statusCode ?? err?.networkError?.statusCode ?? err?.networkError?.status;
  const graphQLExtCode: string | undefined =
    err?.extensions?.code ?? err?.networkError?.result?.errors?.[0]?.extensions?.code;

  if (statusCode === 403) return true;
  if (graphQLExtCode === 'FORBIDDEN') return true;

  const m = message.toLowerCase();
  if (m.includes('forbidden') || m.includes('status code 403')) return true;
  return m.startsWith(RESPONSE_UNAUTHORIZED.toLowerCase());
}

export const createApolloClient = (
  accessToken: string,
  organizationId: number,
  onUnauthorized?: OnUnauthorizedCallback,
  onConnected?: OnConnectedCallback,
  onDisconnected?: OnDisconnectedCallback,
  onUnrecoverableError?: OnUnrecoverableErrorCallback
): [ApolloClient<NormalizedCacheObject>, SubscriptionClient] => {
  const [protocol, host] = config.apiURL.split('://');
  const connectionType = localStorage.getItem('ws-connection-type') || defaultConnectionType;
  if (connectionType !== 'binary' && connectionType !== 'text')
    console.warn(`Unknown WS connection type ${connectionType}, defaulting to ${connectionType}.`);
  const endpoint = `${protocol === 'https' ? 'wss' : 'ws'}://${host}/graphql${
    connectionType === 'binary' ? '?binary' : ''
  }`;
  const wsImpl = connectionType === 'binary' ? MyWebSocket : undefined;

  // Track if we've encountered an unrecoverable error
  let hasUnrecoverableError = false;

  const subscriptionClient = new SubscriptionClient(
    endpoint,
    {
      reconnect: true,
      reconnectionAttempts: Number.MAX_SAFE_INTEGER,
      lazy: false,
      timeout: 30000, // 30 seconds timeout for operations
      minTimeout: 1000, // Start reconnection delay at 1 second
      connectionParams: () => {
        // Prevent reconnection if we've hit an unrecoverable error
        if (hasUnrecoverableError) {
          console.warn('[WS] Blocking connection attempt due to unrecoverable error');
          throw new Error('Unrecoverable error - connection blocked');
        }

        return pickBy(
          {
            accessToken,
            organizationId: isFinite(organizationId) && organizationId,
            clientVersion: config.appVersion,
          } as ConnectionParams,
          identity
        ) as ConnectionParams;
      },
      connectionCallback: (error: Error[], result?: unknown) => {
        if (error && error.length > 0) {
          const first = error[0] as any;

          // Check for unrecoverable errors first (like Unauthorized IP Address)
          if (isUnrecoverableError(first)) {
            console.error('[WS] Unrecoverable connection error detected:', first);
            hasUnrecoverableError = true;
            subscriptionClient.close(false, false); // Close without reconnecting

            // Notify the UI layer about the unrecoverable error
            if (onUnrecoverableError) {
              onUnrecoverableError(first);
            }

            // Also trigger onUnauthorized for backward compatibility
            if (onUnauthorized) {
              onUnauthorized(first, result);
            }
            return;
          }

          // Handle other forbidden/unauthorized errors
          if (isForbiddenOrUnauthorized(first)) {
            subscriptionClient.close();
            if (onUnauthorized) {
              onUnauthorized(first, result);
            }
          }
        }
      },
    },
    wsImpl
  );

  // Intercept WebSocket messages to detect unrecoverable errors
  // Hook into the subscription client's message processing
  subscriptionClient.use([
    {
      applyMiddleware(options, next) {
        next();
      },
    },
  ]);

  // Override the processReceivedData method to intercept messages
  const originalProcessReceivedData = (subscriptionClient as any).processReceivedData;
  if (originalProcessReceivedData) {
    (subscriptionClient as any).processReceivedData = function (receivedData: any) {
      try {
        const parsedMessage = JSON.parse(receivedData);

        // Check for connection_error messages
        if (parsedMessage.type === 'connection_error') {
          console.error('[WS] Received connection_error message:', parsedMessage);

          // Create error object from message
          const errorData = {
            type: parsedMessage.type,
            payload: parsedMessage.payload,
            message: parsedMessage.payload,
          };

          if (isUnrecoverableError(errorData)) {
            console.error('[WS] Unrecoverable error detected - preventing reconnection');

            // Set flag to prevent reconnection
            hasUnrecoverableError = true;

            // Disable reconnection on the client
            (subscriptionClient as any).reconnect = false;
            (subscriptionClient as any).reconnecting = false;

            // Close the connection
            subscriptionClient.close(false, false);

            // Create proper Error object for callbacks
            const error = new Error(
              typeof parsedMessage.payload === 'string' ? parsedMessage.payload : 'Connection error'
            );
            (error as any).type = parsedMessage.type;
            (error as any).payload = parsedMessage.payload;

            if (onUnrecoverableError) {
              onUnrecoverableError(error);
            }

            if (onUnauthorized) {
              onUnauthorized(error);
            }

            // Don't process this message further
            return;
          }
        }
      } catch (_e) {
        // If parsing fails, let the original handler deal with it
      }

      // Call original handler
      return originalProcessReceivedData.call(this, receivedData);
    };
  }

  if (onConnected) {
    subscriptionClient.onConnected(onConnected);
    subscriptionClient.onReconnected(onConnected);
  }

  if (onDisconnected) {
    subscriptionClient.onDisconnected(onDisconnected);
  }

  const cache = new InMemoryCache({
    typePolicies: {
      Query: {
        fields: {
          dailyPhysicians: {
            merge(_existing, incoming) {
              return incoming; // Always replace with fresh data
            },
          },
          dailyAnesthesiologists: {
            merge(_existing, incoming) {
              return incoming; // Always replace with fresh data
            },
          },
        },
      },
      Room: {
        fields: {
          patients: {
            merge(_existing = [], incoming = []) {
              // Always use incoming data (this prevents the cache warning)
              // This is safe because we want fresh patient data on each update
              return incoming;
            },
          },
          anesthesiologistShifts: {
            merge(_existing, incoming) {
              return incoming; // Always replace with fresh data
            },
          },
        },
      },
      Patient: {
        fields: {
          status: {
            // CRITICAL: Prefer subscription data over query data
            // Subscriptions have fresher data than queries due to 2s backend cache
            merge(_existing, incoming) {
              // Always prefer incoming (latest write wins)
              // This ensures subscription updates aren't overwritten by stale query data
              return incoming;
            },
          },
          events: {
            // CRITICAL: Replace events instead of merging
            // PatientEventsType has no id field, so Apollo can't merge it properly
            // Always use the latest events data from subscriptions
            merge(existing, incoming) {
              return incoming;
            },
          },
          log: {
            // CRITICAL: Replace log array instead of merging
            // PatientLog entries come from subscription with complete history
            // Always use the latest log data to ensure UI reflects current state
            merge(existing, incoming) {
              return incoming; // Always use fresh data from subscription
            },
          },
        },
      },
      Procedure: {
        fields: {
          staffShifts: {
            // CRITICAL: Always replace staffShifts array to prevent duplicates
            // Apollo can't properly normalize StaffShift objects without unique keys
            merge(existing, incoming) {
              return incoming; // Always use fresh data from subscription/query
            },
          },
          assignedVendors: {
            // CRITICAL: Always replace assignedVendors array to prevent cache data loss
            // This prevents the "Cache data may be lost" warning and page flashing
            // Vendor updates come via subscription with complete vendor data
            merge(existing, incoming) {
              return incoming; // Always use fresh data from subscription/query
            },
          },
          vendors: {
            // CRITICAL: Always replace vendors array for live updates
            merge(existing, incoming) {
              return incoming; // Always use fresh data from subscription/query
            },
          },
          physicianColor: {
            // CRITICAL: Always replace physicianColor for instant color updates
            // Prevents subscription updates from being blocked by stale cache
            merge(_existing, incoming) {
              return incoming;
            },
          },
          color: {
            // CRITICAL: Always replace color for instant color updates
            merge(_existing, incoming) {
              return incoming;
            },
          },
        },
        merge(existing, incoming, { mergeObjects }) {
          if (!existing) return incoming;
          if (!incoming) return existing;
          return mergeObjects(existing, incoming);
        },
      },
      Subscription: {
        fields: {
          scheduleUpdatedIncremental: {
            // CRITICAL: Merge policy for incremental ECST updates
            // Incoming data is a single-item array with the updated procedure
            // We don't store subscription results - they update the cache via writeFragment
            merge(_existing, incoming) {
              // Always use incoming data (subscription results are transient)
              // The real cache update happens via writeFragment in useScheduleSubscription
              return incoming;
            },
          },
        },
      },
    },
  });

  let gcInterval: ReturnType<typeof setInterval> | undefined;
  let heartbeatInterval: ReturnType<typeof setInterval> | undefined;

  // Client-side heartbeat: Send periodic message to keep server-side SessionActivity alive
  // This prevents the 2-minute CONNECTION_TIMEOUT cleanup for idle display screens
  // The server updates SessionActivity on ANY incoming message, so we send a lightweight 'stop'
  // message with an invalid operation ID (which the server ignores but still processes)
  const startHeartbeat = () => {
    // Clear any existing heartbeat
    if (heartbeatInterval) {
      clearInterval(heartbeatInterval);
    }

    // Send heartbeat every 45 seconds (well under the 2-minute server timeout)
    heartbeatInterval = setInterval(() => {
      if (subscriptionClient.status === 1) {
        // WebSocket.OPEN
        try {
          // Send a 'stop' message for a non-existent operation
          // This is the lightest possible message that still triggers SessionActivity
          // The server's wireTap processes ALL incoming messages, updating the activity timestamp
          (subscriptionClient as any).sendMessage(undefined, 'ka_response', null);
        } catch (_e) {
          // Silently ignore errors - connection might be closing
        }
      }
    }, 45000); // 45 seconds
  };

  const stopHeartbeat = () => {
    if (heartbeatInterval) {
      clearInterval(heartbeatInterval);
      heartbeatInterval = undefined;
    }
  };

  subscriptionClient.onConnected(() => {
    gcInterval = setInterval(
      () => {
        cache.gc();
      },
      1000 * 60 * 60 // Run garbage collection every hour
    );
    startHeartbeat();
  });

  subscriptionClient.onReconnected(() => {
    // Don't process reconnection if we have an unrecoverable error
    if (hasUnrecoverableError) {
      console.warn('[WS] Ignoring reconnection - unrecoverable error detected');
      subscriptionClient.close(false, false);
      return;
    }

    // Restart heartbeat on reconnection
    startHeartbeat();

    console.warn('[WS] ⚠️ RECONNECTED - This will trigger full refetch!', {
      timestamp: new Date().toISOString(),
      activeOperations: Object.keys(subscriptionClient.operations).length,
    });

    // Notify React hooks to re-subscribe
    // This is critical because unsubscribeAll() below will kill all subscriptions
    // and React hooks need to know to re-establish them
    if (window.__notifyWebSocketReconnection) {
      window.__notifyWebSocketReconnection();
    }

    // Re-subscribe all active subscriptions
    const queued = Object.values(subscriptionClient.operations).map(({ options, handler }) => ({
      options,
      handler,
    }));

    subscriptionClient.unsubscribeAll();

    queued.forEach(({ options, handler }) => {
      const observable = subscriptionClient.request(options);
      observable.subscribe({
        next: (payload: unknown) => handler(null as any, payload),
        error: (err: Error) => handler([err], null),
        complete: () => handler(null as any, null),
      });
    });

    // Refetch all active queries to ensure no data was missed during disconnection
    // This is safe now that useMemo is removed from denormalization
    // Live updates work instantly, and refetch is just a safety net
    console.info('[WS] ✅ Reconnected - refetching to ensure data consistency');

    // Wrap refetchQueries in try-catch to prevent Apollo errors from breaking reconnection
    try {
      apolloClient
        .refetchQueries({
          include: 'active',
        })
        .then(() => {
          console.info('[WS] ✅ Refetch complete');
        })
        .catch(err => {
          console.error('[WS] Failed to refetch queries on reconnection:', err);
          // Don't let refetch errors break the reconnection process
          console.info('[WS] ⚠️ Continuing despite refetch failure - subscriptions are working');
        });
    } catch (error) {
      console.error('[WS] Critical error during refetchQueries:', error);
      console.info('[WS] ⚠️ Continuing despite critical error - subscriptions are working');
    }
  });

  subscriptionClient.onDisconnected(() => {
    if (gcInterval) {
      clearInterval(gcInterval);
    }
    stopHeartbeat();
  });

  const apolloClient = new ApolloClient({
    link: ApolloLink.from([
      new DurableMutationsLink(new LocalStorageOperationsRepository(String(organizationId))),
      new RetryLink({
        delay: {
          initial: 300,
          max: 5000,
          jitter: true,
        },
        attempts: {
          max: 3, // Only retry 3 times (not 10)
          retryIf: (error: RetryableError | undefined, operation) => {
            // Only retry mutations that have actual errors
            if (!isMutation(operation) || !error) {
              return false;
            }

            // Don't retry on GraphQL validation errors (4xx)
            if (error.statusCode && error.statusCode >= 400 && error.statusCode < 500) {
              console.warn('[RetryLink] Not retrying mutation (validation error):', operation.operationName, error);
              return false;
            }

            // Retry on network errors, timeouts, 5xx errors
            const isRetryable =
              error.message?.includes('Network') ||
              error.message?.includes('timeout') ||
              error.message?.includes('Failed to fetch') ||
              (error.statusCode !== undefined && error.statusCode >= 500);

            if (isRetryable) {
              console.log('[RetryLink] Retrying mutation:', operation.operationName, 'due to:', error.message);
            }

            return isRetryable;
          },
        },
      }),
      new WebSocketLink(subscriptionClient),
    ]),
    cache,
  });

  return [apolloClient, subscriptionClient];
};
